import * as Joi from "joi";
import { Moment } from 'moment';
import {
	Column,
	Entity,
	OneToMany,
	OneToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Base from '../base.entity';
import CmPermission from '../cm-permissions/cm-permission.entity';
import HaPermission from '../ha-permissions/ha-permission.entity';
import InventoryFieldSetting from '../inventory-field-settings/inventory-field-setting.entity';
import MuPermission from '../mu-permissions/mu-permission.entity';
import PdMessageRecipient from '../pd-message-recipients/pd-message-recipients.entity';
import PdMessage from '../pd-messages/pd-messages.entity';
import Permission from '../permissions/permission.entity';
import PrPermission from '../pr-permissions/pr-permission.entity';
import SetPermission from '../set-permissions/set-permission.entity';

@Entity('users')
export default class User extends Base {
	@PrimaryGeneratedColumn()
	id: number;

	@Column('varchar')
	username: string;

	@Column('varchar')
	password: string;

	@Column('varchar')
	google_2fa_secret: string;

	@Column('varchar')
	email: string;

	@Column('varchar')
	salt: string;

	@Column('int')
	current: number;

	@Column('int')
	cur_dep: number;

	@Column('int')
	cur_ep: number;

	@Column('varchar')
	first_name: string;

	@Column('varchar')
	last_name: string;

	@Column('varchar')
	phone: string;

	@Column('varchar')
	title: string;

	@Column('varchar')
	how_hear: string;

	@Column('varchar')
	referral: string;

	@Column('int')
	can_create: number;

	@Column('varchar')
	smart_id: string;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	smart_id_mfa_enabled: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	smart_id_federated: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_log_in: Moment;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	show_thumbnails: boolean;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	approved: boolean;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated: Moment;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column('varchar')
	activated: string;

	@OneToMany((type) => Permission, (permission) => permission.user)
	permissions: Permission[];

	@OneToMany((type) => CmPermission, (cmPermission) => cmPermission.user)
	cmPermissions: CmPermission[];

	@OneToMany((type) => HaPermission, (haPermission) => haPermission.user)
	haPermissions: HaPermission[];

	@OneToMany((type) => MuPermission, (muPermission) => muPermission.user)
	muPermissions: MuPermission[];

	@OneToMany((type) => PrPermission, (prPermission) => prPermission.user)
	prPermissions: PrPermission[];

	@OneToMany((type) => SetPermission, (setPermission) => setPermission.user)
	setPermissions: SetPermission[];

	@OneToOne(
		(type) => InventoryFieldSetting,
		(inventoryFieldSetting) => inventoryFieldSetting.user
	)
	inventoryFieldSetting: InventoryFieldSetting;

	@OneToMany((type) => PdMessage, (pdMessage) => pdMessage.sender)
	pdMessage: PdMessage[];

	@OneToMany(
		(type) => PdMessageRecipient,
		(pdMessageRecipient) => pdMessageRecipient.recipient
	)
	pdMessageRecipient: PdMessageRecipient[];

	/**
	 * Hash for intercom identity verification
	 * only set in permissions/get route
	 */
	intercom_hash: string;

	static rules() {
		return {
			id: Joi.number().integer(),
			username: Joi.string().max(45),
			password: Joi.string().max(128),
			google_2fa_secret: Joi.string().max(16).allow(null),
			email: Joi.string().email().max(45),
			salt: Joi.string().max(8),
			current: Joi.number().integer(),
			cur_dep: Joi.number().integer(),
			cur_ep: Joi.number().integer(),
			first_name: Joi.string().max(45),
			last_name: Joi.string().max(45),
			phone: Joi.string().max(20),
			title: Joi.string().max(300),
			how_hear: Joi.string().max(500),
			referral: Joi.string().max(500),
			can_create: Joi.number().integer().allow(null),
			last_log_in: Joi.date().allow(null),
			show_thumbnails: Joi.boolean(),
			approved: Joi.boolean().allow(null),
			last_updated: Joi.date().allow(null),
			date_created: Joi.date().allow(null),
			activated: Joi.string().max(30),
			smart_id: Joi.string().max(36).allow(null),
			smart_id_mfa_enabled: Joi.boolean().allow(null),
			smart_id_federated: Joi.boolean().allow(null),
		};
	}

	toJSON() {
		return {
			id: this.id,
			username: this.username,
			email: this.email,
			current: this.current,
			cur_dep: this.cur_dep,
			cur_ep: this.cur_ep,
			first_name: this.first_name,
			last_name: this.last_name,
			phone: this.phone,
			title: this.title,
			how_hear: this.how_hear,
			referral: this.referral,
			can_create: this.can_create,
			last_log_in: this.last_log_in,
			show_thumbnails: this.show_thumbnails,
			approved: this.approved,
			last_updated: this.last_updated,
			date_created: this.date_created,
		};
	}

	assign(props?: GenPartial<User>) {
		super.assign(props);

		if (props) {
			if (typeof props.last_log_in !== 'undefined') {
				this.last_log_in = momentTransformer.from(props.last_log_in);
			}
			if (typeof props.show_thumbnails !== 'undefined') {
				this.show_thumbnails = booleanTransformer.from(props.show_thumbnails);
			}
			if (typeof props.approved !== 'undefined') {
				this.approved = booleanTransformer.from(props.approved);
			}
			if (typeof props.last_updated !== 'undefined') {
				this.last_updated = momentTransformer.from(props.last_updated);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<User>) {
		super(props);
	}

	getFullName(): string {
		return `${this.first_name} ${this.last_name}`;
	}
}
